import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Users,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle2,
  Lightbulb,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Clock,
  UserPlus,
  Star,
  Zap,
  Loader2,
  X,
} from 'lucide-react';
import clsx from 'clsx';
import { coverageApi, shiftInstancesApi, type LaborForecast, type OptimizationSuggestion, type ShiftInstance } from '../../services/api';

interface CoverageGap {
  date: string;
  shiftInstanceId: string;
  shiftName: string;
  role: string;
  required: number;
  assigned: number;
  gap: number;
  startTime: string;
  endTime: string;
}

interface CoverageCandidate {
  employeeId: string;
  employeeName: string;
  score: number;
  factors: {
    availability: number;
    roleMatch: number;
    laborCompliance: number;
    costEfficiency: number;
    fairness: number;
    reliability: number;
  };
  reasoning: string[];
  isRecommended?: boolean;
}

interface CoverageRecommendationsPanelProps {
  forecasts: LaborForecast[];
  suggestions: OptimizationSuggestion[];
  shiftInstances: ShiftInstance[];
  isExpanded: boolean;
  onToggleExpand: () => void;
  selectedWeekStart: string;
  selectedWeekEnd: string;
  onAssignmentChange?: () => void;
}

export default function CoverageRecommendationsPanel({
  forecasts,
  suggestions,
  shiftInstances,
  isExpanded,
  onToggleExpand,
  selectedWeekStart,
  selectedWeekEnd,
  onAssignmentChange,
}: CoverageRecommendationsPanelProps) {
  const queryClient = useQueryClient();
  const [expandedGap, setExpandedGap] = useState<string | null>(null);
  const [assigningTo, setAssigningTo] = useState<string | null>(null);

  // Fetch coverage gaps for the selected week
  const { data: gapsData, isLoading: isLoadingGaps } = useQuery({
    queryKey: ['coverage-gaps', selectedWeekStart, selectedWeekEnd],
    queryFn: () => coverageApi.getGaps(selectedWeekStart, selectedWeekEnd),
    enabled: isExpanded,
    staleTime: 30000,
  });

  // Fetch candidates for expanded gap
  const { data: candidatesData, isLoading: isLoadingCandidates } = useQuery({
    queryKey: ['coverage-candidates', expandedGap],
    queryFn: () => expandedGap ? coverageApi.getSubstitutes(expandedGap, 5) : null,
    enabled: !!expandedGap,
    staleTime: 30000,
  });

  // Assign mutation
  const assignMutation = useMutation({
    mutationFn: ({ assignmentId, employeeId }: { assignmentId: string; employeeId: string }) =>
      coverageApi.assignCoverage(assignmentId, employeeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coverage-gaps'] });
      queryClient.invalidateQueries({ queryKey: ['shift-instances'] });
      setExpandedGap(null);
      setAssigningTo(null);
      onAssignmentChange?.();
    },
  });

  // Apply the labor forecast: draft shifts / staffing targets for the week
  const [applySummary, setApplySummary] = useState<{ created: number; updated: number; skipped: number; results: Array<{ date: string; action: string; detail: string }> } | null>(null);
  const applyForecastMutation = useMutation({
    mutationFn: async () => {
      const res = await shiftInstancesApi.applyForecast(selectedWeekStart, selectedWeekEnd);
      return (res.data?.data || res.data) as { created: number; updated: number; skipped: number; results: Array<{ date: string; action: string; detail: string }> };
    },
    onSuccess: (summary) => {
      setApplySummary(summary);
      queryClient.invalidateQueries({ queryKey: ['schedule-builder-instances'] });
      queryClient.invalidateQueries({ queryKey: ['shift-instances'] });
      queryClient.invalidateQueries({ queryKey: ['coverage-gaps'] });
      onAssignmentChange?.();
    },
    onError: () => {
      setApplySummary({ created: 0, updated: 0, skipped: 0, results: [{ date: '', action: 'skipped', detail: "The forecast couldn't be applied just now — try again in a moment." }] });
    },
  });

  // Calculate staffing comparison for the week
  const staffingAnalysis = useMemo(() => {
    const analysis: Record<string, {
      forecast: LaborForecast | null;
      scheduledFoh: number;
      scheduledBoh: number;
      status: 'adequate' | 'understaffed' | 'overstaffed' | 'no-forecast';
    }> = {};

    for (const shift of shiftInstances) {
      const date = shift.date;
      if (!analysis[date]) {
        analysis[date] = {
          forecast: forecasts.find(f => f.forecastDate === date) || null,
          scheduledFoh: 0,
          scheduledBoh: 0,
          status: 'no-forecast',
        };
      }

      for (const assignment of shift.assignments || []) {
        const role = assignment.role?.toLowerCase() || '';
        const isBoh = ['cook', 'chef', 'prep', 'dish', 'kitchen'].some(r => role.includes(r));
        if (isBoh) {
          analysis[date].scheduledBoh++;
        } else {
          analysis[date].scheduledFoh++;
        }
      }
    }

    for (const date of Object.keys(analysis)) {
      const day = analysis[date];
      if (day.forecast) {
        const fohDiff = day.scheduledFoh - day.forecast.recommendedFohCount;
        const bohDiff = day.scheduledBoh - day.forecast.recommendedBohCount;
        const totalDiff = fohDiff + bohDiff;

        if (totalDiff < -2) {
          day.status = 'understaffed';
        } else if (totalDiff > 2) {
          day.status = 'overstaffed';
        } else {
          day.status = 'adequate';
        }
      }
    }

    return analysis;
  }, [forecasts, shiftInstances]);

  // Count statuses
  const statusCounts = useMemo(() => {
    const counts = { adequate: 0, understaffed: 0, overstaffed: 0, noForecast: 0 };
    for (const date of Object.keys(staffingAnalysis)) {
      const status = staffingAnalysis[date].status;
      if (status === 'adequate') counts.adequate++;
      else if (status === 'understaffed') counts.understaffed++;
      else if (status === 'overstaffed') counts.overstaffed++;
      else counts.noForecast++;
    }
    return counts;
  }, [staffingAnalysis]);

  // Get actionable gaps (from API)
  const gaps = gapsData?.data?.gaps || [];
  const totalGapCount = gaps.reduce((sum: number, g: CoverageGap) => sum + g.gap, 0);

  // Calculate total expected covers
  const totalExpectedCovers = forecasts.reduce((sum, f) => sum + (f.expectedCovers || 0), 0);

  const handleAssign = (assignmentId: string, employeeId: string) => {
    setAssigningTo(employeeId);
    assignMutation.mutate({ assignmentId, employeeId });
  };

  // Helper to format time
  const formatTime = (iso: string) => {
    const date = new Date(iso);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' });
  };

  // Helper to format date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  if (forecasts.length === 0 && gaps.length === 0) {
    return null;
  }

  return (
    <div className="bg-white border-t border-slate-200">
      {/* Header - Always visible */}
      <button
        onClick={onToggleExpand}
        className="w-full px-4 py-2 flex items-center justify-between hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-amber-500" />
            <span className="text-sm font-medium text-slate-700">Coverage & Staffing</span>
          </div>

          {/* Quick status badges */}
          <div className="flex items-center gap-2">
            {totalGapCount > 0 && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                <AlertTriangle className="w-3 h-3" />
                {totalGapCount} position{totalGapCount !== 1 ? 's' : ''} to fill
              </span>
            )}
            {statusCounts.understaffed > 0 && totalGapCount === 0 && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                <TrendingDown className="w-3 h-3" />
                {statusCounts.understaffed} day{statusCounts.understaffed !== 1 ? 's' : ''} understaffed
              </span>
            )}
            {statusCounts.adequate > 0 && statusCounts.understaffed === 0 && totalGapCount === 0 && (
              <span className="flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                <CheckCircle2 className="w-3 h-3" />
                Fully staffed
              </span>
            )}
          </div>

          {totalExpectedCovers > 0 && (
            <div className="text-xs text-slate-500">
              ~{totalExpectedCovers} covers expected
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 text-slate-400">
          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          {/* Apply forecast → draft shifts / staffing targets */}
          <div className="flex items-start justify-between gap-3 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5">
            <div className="text-xs text-slate-600">
              <span className="font-medium text-slate-800">Apply this week's forecast</span> — creates draft shifts (from your templates) with recommended staffing targets. Staff see nothing until you publish.
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); applyForecastMutation.mutate(); }}
              disabled={applyForecastMutation.isPending}
              className="btn btn-sm btn-primary shrink-0 inline-flex items-center gap-1.5"
            >
              {applyForecastMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
              {applyForecastMutation.isPending ? 'Applying…' : 'Apply forecast'}
            </button>
          </div>

          {applySummary && (
            <div className="rounded-lg border border-slate-200 px-3 py-2.5 text-xs space-y-1">
              <div className="flex items-center justify-between">
                <p className="font-medium text-slate-800">
                  {applySummary.created} draft shift{applySummary.created !== 1 ? 's' : ''} created · {applySummary.updated} updated · {applySummary.skipped} left alone
                </p>
                <button onClick={() => setApplySummary(null)} aria-label="Dismiss" className="text-slate-400 hover:text-slate-600">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              {applySummary.results.map((r, i) => (
                <p key={i} className="text-slate-500">
                  {r.date && <span className="font-medium">{r.date}: </span>}{r.detail}
                </p>
              ))}
            </div>
          )}

          {/* Actionable Coverage Gaps */}
          {gaps.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-500" />
                Open Positions to Fill
              </h4>

              <div className="space-y-2">
                {gaps.slice(0, 5).map((gap: CoverageGap) => {
                  const gapKey = `${gap.shiftInstanceId}-${gap.role}`;
                  const isExpanded = expandedGap === gap.shiftInstanceId;
                  const candidates = isExpanded ? (candidatesData?.data?.candidates || []) : [];

                  return (
                    <div
                      key={gapKey}
                      className={clsx(
                        'border rounded-lg overflow-hidden transition-all',
                        isExpanded ? 'border-indigo-300 bg-indigo-50/50' : 'border-slate-200 bg-white'
                      )}
                    >
                      {/* Gap Header */}
                      <div
                        className={clsx(
                          'flex items-center justify-between p-3 cursor-pointer',
                          isExpanded ? 'bg-indigo-100/50' : 'hover:bg-slate-50'
                        )}
                        onClick={() => setExpandedGap(isExpanded ? null : gap.shiftInstanceId)}
                      >
                        <div className="flex items-center gap-3">
                          <div className={clsx(
                            'w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold',
                            gap.gap >= 3 ? 'bg-red-100 text-red-700' :
                            gap.gap >= 2 ? 'bg-amber-100 text-amber-700' :
                            'bg-yellow-100 text-yellow-700'
                          )}>
                            +{gap.gap}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-slate-800">
                              {gap.role} • {gap.shiftName}
                            </div>
                            <div className="text-xs text-slate-500">
                              {formatDate(gap.date)} • {formatTime(gap.startTime)} - {formatTime(gap.endTime)}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-500">
                            {gap.assigned}/{gap.required} assigned
                          </span>
                          <UserPlus className={clsx(
                            'w-4 h-4 transition-transform',
                            isExpanded ? 'text-indigo-600 rotate-45' : 'text-slate-400'
                          )} />
                        </div>
                      </div>

                      {/* Candidates Panel */}
                      {isExpanded && (
                        <div className="p-3 border-t border-indigo-200 bg-white">
                          {isLoadingCandidates ? (
                            <div className="flex items-center justify-center py-4 text-slate-500">
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                              Finding best candidates...
                            </div>
                          ) : candidates.length === 0 ? (
                            <div className="text-center py-4 text-slate-500 text-sm">
                              No available candidates found. Consider:
                              <ul className="mt-2 text-xs text-left list-disc list-inside">
                                <li>Checking employees from other roles</li>
                                <li>Reaching out to part-time staff</li>
                              </ul>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <div className="text-xs font-medium text-slate-600 mb-2">
                                Top candidates (ranked by fit):
                              </div>
                              {candidates.map((candidate: CoverageCandidate, index: number) => (
                                <div
                                  key={candidate.employeeId}
                                  className={clsx(
                                    'flex items-center justify-between p-2 rounded-lg border',
                                    index === 0 ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-slate-50'
                                  )}
                                >
                                  <div className="flex items-center gap-3">
                                    <div className={clsx(
                                      'w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold',
                                      index === 0 ? 'bg-emerald-500 text-white' :
                                      index === 1 ? 'bg-slate-300 text-slate-700' :
                                      'bg-slate-200 text-slate-600'
                                    )}>
                                      {index + 1}
                                    </div>
                                    <div>
                                      <div className="text-sm font-medium text-slate-800 flex items-center gap-1">
                                        {candidate.employeeName}
                                        {index === 0 && <Star className="w-3 h-3 text-amber-500" />}
                                      </div>
                                      <div className="text-xs text-slate-500">
                                        Score: {Math.round(candidate.score * 100)}%
                                        {candidate.reasoning?.[0] && (
                                          <span className="text-slate-400"> • {candidate.reasoning[0]}</span>
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // We need to find the assignment ID from the shift instance
                                      // For now, we'll use the shiftInstanceId
                                      handleAssign(gap.shiftInstanceId, candidate.employeeId);
                                    }}
                                    disabled={assignMutation.isPending && assigningTo === candidate.employeeId}
                                    className={clsx(
                                      'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                                      index === 0
                                        ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                                        : 'bg-slate-200 text-slate-700 hover:bg-slate-300',
                                      assignMutation.isPending && assigningTo === candidate.employeeId && 'opacity-50'
                                    )}
                                  >
                                    {assignMutation.isPending && assigningTo === candidate.employeeId ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                      'Assign'
                                    )}
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {gaps.length > 5 && (
                  <div className="text-xs text-slate-500 text-center">
                    +{gaps.length - 5} more positions need coverage
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Daily breakdown */}
          {forecasts.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-slate-700">Weekly Staffing Overview</h4>
              <div className="grid grid-cols-7 gap-2">
                {Object.entries(staffingAnalysis)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([date, data]) => {
                    const dayName = new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short' });
                    const dayNum = new Date(date + 'T12:00:00').getDate();

                    return (
                      <div
                        key={date}
                        className={clsx(
                          'p-2 rounded-lg border text-center text-xs',
                          data.status === 'adequate' && 'bg-emerald-50 border-emerald-200',
                          data.status === 'understaffed' && 'bg-red-50 border-red-200',
                          data.status === 'overstaffed' && 'bg-amber-50 border-amber-200',
                          data.status === 'no-forecast' && 'bg-slate-50 border-slate-200'
                        )}
                      >
                        <div className="font-medium text-slate-700">{dayName} {dayNum}</div>
                        {data.forecast ? (
                          <>
                            <div className="text-slate-500 mt-1">
                              ~{data.forecast.expectedCovers} covers
                            </div>
                            <div className="flex items-center justify-center gap-1 mt-1">
                              <Users className="w-3 h-3" />
                              <span>
                                {data.scheduledFoh + data.scheduledBoh}/
                                {data.forecast.recommendedFohCount + data.forecast.recommendedBohCount}
                              </span>
                            </div>
                            {data.status === 'understaffed' && (
                              <div className="text-red-600 font-medium mt-1">
                                <TrendingDown className="w-3 h-3 inline" /> Need more
                              </div>
                            )}
                            {data.status === 'overstaffed' && (
                              <div className="text-amber-600 font-medium mt-1">
                                <TrendingUp className="w-3 h-3 inline" /> Extra staff
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="text-slate-400 mt-1">No forecast</div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Optimization Suggestions */}
          {suggestions.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-slate-700">Suggestions</h4>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {suggestions.slice(0, 3).map((suggestion, i) => (
                  <div
                    key={i}
                    className={clsx(
                      'flex items-start gap-3 p-2 rounded-lg border text-xs',
                      suggestion.type === 'understaffed' || suggestion.type === 'overtime_risk'
                        ? 'bg-red-50 border-red-200'
                        : 'bg-amber-50 border-amber-200'
                    )}
                  >
                    {suggestion.type === 'understaffed' || suggestion.type === 'overtime_risk' ? (
                      <AlertTriangle className="w-3 h-3 text-red-500 mt-0.5 flex-shrink-0" />
                    ) : (
                      <Lightbulb className="w-3 h-3 text-amber-500 mt-0.5 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-800">{suggestion.message}</div>
                      {suggestion.suggestion && (
                        <div className="text-slate-600 mt-0.5">{suggestion.suggestion}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
